/**
 * Shared TypeScript types for the Aptean SSP frontend.
 */
import type { AppriseServerSummary, AppriseVmConfig, AppriseVmOutputs, DeploymentTracks, VmSizing } from './appriseVm'

export * from './appriseVm'
import type {
  PrimaryProductRef,
  ProvisioningStatus,
  ServerMode,
  WorkwiseSummary,
} from './workwise'

// ─── Enums ────────────────────────────────────────────────────────────────────

export type CloudProvider = 'azure' | 'ibm'

export type TShirtSize = 'S' | 'M' | 'L' | 'XL'

/**
 * What a selected product's size is: a T-shirt size for the products sized by the platform's own size configs, and the RDS pool tier
 * (STD, PREM, HIGH or DED: see data/sizeCatalog.ts) for the product lines, Traverse and Process Pro.
 */
export type ProductSize = TShirtSize | 'STD' | 'PREM' | 'HIGH' | 'DED'

export const isTShirtSize = (size: string): size is TShirtSize => size === 'S' || size === 'M' || size === 'L' || size === 'XL'

export type DeploymentStatus =
  | 'pending'
  | 'validating'
  | 'provisioning_network'
  | 'provisioning_infra'
  // Apprise-on-VM: Terraform plan → manual approval → apply
  | 'planning_infra'
  | 'awaiting_plan_approval'
  | 'applying_infra'
  // Apprise-on-VM: after apply the VM is verified through the Azure control plane;
  // the deployment ends in infra_ready (NOT completed — the app is never installed).
  | 'verifying_server'
  | 'infra_ready'
  | 'deploying_app'
  | 'configuring'
  | 'configuring_haproxy'
  | 'configuring_cloudflare'
  | 'post_deploy_config'
  | 'configuring_integrations'
  | 'switching_traffic'
  | 'running_health_checks'
  | 'completed'
  | 'failed'
  | 'rolled_back'
  | 'cancelled'
  | 'destroying'
  | 'destroyed'

export type PipelineType = 'github_actions' | 'azure_devops'

export interface DeploymentStage {
  id: number
  stage: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'auto_fixing'
  started_at: string | null
  completed_at: string | null
  log_output: string
  error_message: string
  auto_fix_suggestion: string
  auto_fix_applied: boolean
  order: number
}

export interface BlueGreenSwitch {
  id: number
  from_slot: string
  to_slot: string
  switched_at: string
  notes: string
}

export type Environment = 'dev' | 'stg' | 'qa' | 'uat' | 'demo' | 'production' | 'uat_usgov' | 'prod_usgov'

export type ApprovalStatus = 'pending_approval' | 'approved' | 'rejected'

export type DeploymentStrategy = 'rolling' | 'blue_green' | 'canary' | 'recreate'

export type ProductFamily =
  | 'appcentral'
  | 'aip'
  | 'pay_edi'
  | 'm2m'
  | 'agent_central'
  | 'genai'
  | 'fixed_assets'
  | 'apprise'

export type LogLevel = 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG'

// ─── Product Types ─────────────────────────────────────────────────────────────

export interface MicroserviceRef {
  id: number
  name: string
  slug: string
  available_sizes: TShirtSize[]
}

export interface Product {
  id: number
  name: string
  slug: string
  family: ProductFamily
  description: string
  version: string
  helm_chart: string
  container_image: string
  pipeline_url?: string
  is_active: boolean
  requires?: number[]
  available_sizes: TShirtSize[]
  parent: number | null
  microservices: MicroserviceRef[]
}

export interface SizeResources {
  cpu_request: string
  cpu_limit: string
  memory_request: string
  memory_limit: string
  storage_gb: number
  storage_class?: string
  db_tier: string
  db_vcores?: number
  min_replicas: number
  max_replicas: number
  estimated_monthly_cost_usd: number
  // Infrastructure (Azure VM) sizing — null when the size has no VM profile.
  vm?: VmSizing | null
  /** How many users can log in at this size (informational, Admin Settings); null or absent when not set. */
  max_users?: number | null
}

export interface TShirtSizeConfig {
  id: number
  product: number
  product_name: string
  size: TShirtSize
  resources: SizeResources
}

export interface ProductBundle {
  id: number
  name: string
  description: string
  products: number[]
  product_names: string[]
  default_size: TShirtSize
}

// ─── Deployment Types ──────────────────────────────────────────────────────────

export interface DeploymentItem {
  id?: number
  product: number
  product_name?: string
  product_family?: ProductFamily
  /** A T-shirt size, or for a product line (Traverse, Process Pro) its RDS pool tier. */
  size: ProductSize
  namespace?: string
  helm_release?: string
  status?: DeploymentStatus
  config_overrides?: Record<string, unknown>
  environment_variables?: Record<string, string>
  feature_flags?: Record<string, boolean>
}

export interface ServiceDeskTicket {
  id: number
  ticket_number: string
  title: string
  description: string
  priority: string
  created_at: string
  external_url: string
}

export interface DeploymentRequest {
  id: number
  name: string
  description?: string
  tenant_id: string
  customer_name: string
  cloud_provider: CloudProvider
  environment: Environment
  azure_region: string
  secondary_region?: string
  // IBM Cloud
  ibm_region?: string
  ibm_region_secondary?: string
  ibm_resource_group?: string
  ibm_account_id?: string
  ibm_vpc_name?: string
  iks_cluster_name?: string
  strategy: DeploymentStrategy
  status: DeploymentStatus
  requested_by: number
  requested_by_name?: string
  created_at: string
  updated_at: string
  started_at?: string
  completed_at?: string
  // The AKS cluster of a cluster deployment. Empty for one that builds servers (Traverse, Ross, Apprise), which names its resource group instead.
  aks_cluster?: string
  resource_group?: string
  ingress_url?: string
  error_message?: string
  items: DeploymentItem[]
  // Approval
  approval_required?: boolean
  approval_status?: ApprovalStatus | null
  approved_by_name?: string | null
  approval_notes?: string
  // Service desk
  service_desk_ticket?: string
  service_desk_url?: string
  // Pipeline
  pipeline_type?: PipelineType
  pipeline_run_url?: string
  app_pipeline_run_url?: string
  app_pipeline_run_id?: string
  // Every app-server ERP-install run (primary + secondaries) for a
  // multi-app-server Ross deploy, so the UI shows how many servers are deploying.
  app_pipeline_runs?: AppServerRun[]
  // Ross Ansible bootstrap run (Azure DevOps 2093) — creates FIN/MAN DB users
  // between the infra run and the AptBackOffice app deploy (1522).
  ansible_pipeline_run_url?: string
  ansible_pipeline_run_id?: string
  has_app_stage?: boolean
  // Network
  vnet_name?: string
  vnet_address_space?: string
  subnet_name?: string
  subnet_prefix?: string
  pod_cidr?: string
  service_cidr?: string
  dns_service_ip?: string
  // Application
  application_url?: string
  // Blue-green
  active_slot?: 'blue' | 'green' | ''
  // Auto-fix
  auto_fix_enabled?: boolean
  auto_fix_attempts?: number
  // Stages
  stages?: DeploymentStage[]
  // Pipeline config echoed back by the API, incl. the transient infra→app
  // handoff status (why the ADO run hasn't started yet) and the test skip flag.
  pipeline_config?: {
    ross_app?: RossAppConfig
    app_deploy_detail?: string
    skip_infra?: boolean
    // Ansible bootstrap (ADO 2093) outcome, written by the completion callback.
    ansible_bootstrap?: 'succeeded' | 'failed'
    // Apprise-on-VM resolved + validated configuration, and the apply outputs
    apprise_vm?: AppriseVmConfig
    apprise_vm_outputs?: AppriseVmOutputs
    [key: string]: unknown
  }
  // Apprise-on-VM: independent infra / application tracks and the server summary
  // (both null / absent for other products).
  tracks?: DeploymentTracks | null
  server?: AppriseServerSummary | null
  // Added with the WorkWise topology round. Present on every list row from the
  // real API; `workwise` is null for other products.
  provisioning_status?: ProvisioningStatus
  primary_product?: PrimaryProductRef | null
  created_by?: string
  hostname?: string | null
  workwise?: WorkwiseSummary | null
}

export interface RossAppConfig {
  customer_id: string
  customer_domain: string
  vm_name: string
  erp_version: string
  iaf_version: string
  install_cfb: boolean
  install_scf: boolean
  sp_url: string
  tenant_id?: string
  coid?: string
  // Resolved SQL server FQDN / database name — needed to create the FIN/MAN
  // database users (see ross_db_credentials.py on the backend).
  db_server?: string
  db_name?: string
  [key: string]: unknown
}

export interface CreateDeploymentRequest {
  name: string
  description?: string
  tenant_id: string
  customer_name: string
  cloud_provider: CloudProvider
  environment: Environment
  azure_region?: string
  secondary_region?: string
  vnet_name?: string
  subnet_name?: string
  // IBM Cloud
  ibm_region?: string
  ibm_region_secondary?: string
  ibm_resource_group?: string
  ibm_account_id?: string
  ibm_vpc_name?: string
  strategy: DeploymentStrategy
  approval_required?: boolean
  approval_notes?: string
  pipeline_type?: PipelineType
  pipeline_url?: string
  pipeline_config?: {
    create_public_ip: boolean
    load_balancer_required: boolean
    data_disk_sizes: number[]
    additional_tags: Record<string, string>
    // Ross-specific (infra)
    customer_number?: number
    app_server_count?: number
    create_elastic_pool?: boolean
    create_database?: boolean
    database_name?: string
    // Ross-specific (app deploy → Azure DevOps pipeline 1522)
    ross_app?: RossAppConfig
    // Transient infra→app handoff status (why the ADO run hasn't started yet)
    app_deploy_detail?: string
    // TEST-ONLY: skip infra and trigger app deploy directly
    skip_infra?: boolean
    // Apprise-on-VM (required when an Apprise product is selected)
    apprise_vm?: AppriseVmConfig
  }
  vnet_address_space?: string
  subnet_prefix?: string
  pod_cidr?: string
  service_cidr?: string
  dns_service_ip?: string
  auto_fix_enabled?: boolean
  items: Array<{
    product: number
    size: ProductSize
    config_overrides?: Record<string, unknown>
    environment_variables?: Record<string, string>
    feature_flags?: Record<string, boolean>
  }>
}

export interface DeploymentLog {
  id: number
  deployment: number
  timestamp: string
  level: LogLevel
  source: string
  message: string
}

// ─── Live GitHub Actions run (real dispatched pipeline) ─────────────────────────

export type GhRunStatus = 'queued' | 'in_progress' | 'completed'
export type GhConclusion =
  | 'success' | 'failure' | 'cancelled' | 'skipped'
  | 'timed_out' | 'action_required' | 'neutral' | 'stale' | null

export interface PipelineRunStep {
  name: string
  status: GhRunStatus
  conclusion: GhConclusion
  number: number
  started_at: string | null
  completed_at: string | null
}

export interface PipelineRunJob {
  id: number
  name: string
  status: GhRunStatus
  conclusion: GhConclusion
  html_url: string
  started_at: string | null
  completed_at: string | null
  steps: PipelineRunStep[]
}

// One Ross app-server ERP-install run (pipeline 1522). A multi-app-server deploy
// has one primary + N secondaries, each installing on its own VM.
export interface AppServerRun {
  label: string
  vm: string
  url: string
  primary: boolean
  status?: GhRunStatus | null
  conclusion?: GhConclusion | null
}

export interface PipelineRun {
  id: number
  name: string
  status: GhRunStatus
  conclusion: GhConclusion
  html_url: string
  run_number: number
  event: string
  run_started_at: string | null
  updated_at: string | null
  jobs: PipelineRunJob[]
  // Per-app-server live status (present only for multi-app-server Ross deploys).
  servers?: AppServerRun[]
}

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// ─── Monitoring Types ──────────────────────────────────────────────────────────

export interface HealthCheck {
  name: string
  status: 'healthy' | 'degraded' | 'unhealthy'
  message?: string
  latency_ms?: number
  checked_at: string
}

export interface HealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy'
  checks: HealthCheck[]
  version?: string
  environment?: string
}

export interface CostRecord {
  id: number
  stack: string
  tenant_id: string
  tenant_name: string
  period_start: string
  period_end: string
  cost_usd: number
  budget_usd: number
  variance_usd: number
  resource_group?: string
  azure_subscription?: string
}

export interface QuotaSettings {
  max_concurrent_deployments: number
  deployment_timeout_minutes: number
  max_deployments_per_tenant: number
  enable_auto_rollback: boolean
}

// ─── Filter / Query Types ──────────────────────────────────────────────────────

export interface DeploymentFilters {
  search?: string
  environment?: Environment | ''
  status?: DeploymentStatus | ''
  cloud_provider?: CloudProvider | ''
  page?: number
  page_size?: number
  // WorkWise round: product slug, customer fragment/number, topology modes,
  // provisioning status (comma list) and server-side ordering.
  product?: string
  customer?: string
  db_mode?: ServerMode | ''
  gateway_mode?: ServerMode | ''
  provisioning_status?: string
  ordering?: string
}
